import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges, TemplateRef, ViewChild
} from '@angular/core';
import {Columns, Config, DefaultConfig} from 'ngx-easy-table';
import {get, startCase} from 'lodash-es';
import {ColumnType, DotNestedKeys} from '@shared/helper/reflections';
import {strings} from '@shared/helper/strings';

export function sm<T>(columns: EasyColumns<T>[], includeHeader = true): EasyColumns<T>[] {
    return columns.map(column => ({
        ...column,
        cssClass: { includeHeader, name: 'text-sm', ...column.cssClass }
    }));
}

export interface EasyColumns<T = any> extends Partial<Columns> {
    key: DotNestedKeys<T> | '';
    type?: ColumnType;
    func?: (value, row: T) => string;
    objectKey?: string;
    defaultValue?: string;
}

export interface EasyRow<T> {
    source: T;
}

export interface EasyTableEvent<T> {
    event: string;
    value: {
        colId: number;
        rowId: number;
        key: string;
        row: {
            source: T;
        }
    };
}

function makeId(length) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
        counter += 1;
    }
    return result;
}

@Component({
    selector: 'app-easy-table',
    template: `
        <ng-container *ngIf="rows">
            <ngx-table [configuration]="configuration"
                       [id]="id"
                       [data]="rows"
                       [columns]="cols"
                       [detailsTemplate]="detailsTemplate"
                       [summaryTemplate]="summaryTemplate ? summaryTemplate : (showFooter ? summaryTemplateRef : null)"
                       (event)="onClick($event)"
                       style="cursor: pointer;"
            >
            </ngx-table>
            <ng-template #summaryTemplateRef let-total="total" let-limit="limit" let-page="page">
                <th colspan="5">
                    <span class="mb-2">Total items: {{ total }} </span>
                    <span class="mb-2">limit: {{ limit }} </span>
                    <span class="mb-2">page: {{ page }} </span>
                </th>
            </ng-template>
        </ng-container>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EasyTableComponent<T = any> implements OnInit, OnChanges {
    @ViewChild('deleteTpl', { static: true }) deleteTpl: TemplateRef<any>;

    @Input() configuration: Config = {...DefaultConfig};
    @Input() columns: EasyColumns<T>[];
    @Input() data: T[];
    @Input() detailsTemplate: TemplateRef<any>;
    @Input() summaryTemplate: TemplateRef<any>;
    @Input() showFooter = true;
    id =  makeId(10);

    @Output() rowClick = new EventEmitter<any>();

    rows: EasyRow<T>[];
    cols: EasyColumns<T>[];

    ngOnInit(): void {
        this.buildColumn();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.buildColumn();
        this.render(this.data);
    }

    buildColumn(){
        this.cols = this.columns.map((c, index) => {
            return {
                ...c,
                objectKey: c.key.toString(),
                key: index.toString(),
                title: c.title ?? startCase(c.key.toString())
            } as EasyColumns<T>;
        });
    }

    render(rows: T[]): void {
        this.rows = (rows ?? []).map(row => {
            const result = {source: row};
            this.cols.forEach((column, index) => {
                try {
                    const field = column.objectKey;
                    const rawValue = field ? get(row, field) : undefined;
                    result[index.toString()] = this.transform(column, rawValue, row);
                } catch (e) {
                    console.warn(e);
                }
            });
            return result;
        });
    }

    onClick($event: any) {
        if ($event.event === 'onClick'){
            this.rowClick.emit($event);
        }else {
            console.log('Discard event', $event.event);
        }
    }

    private transform(column: EasyColumns<T>, rawValue, row: T): string | undefined {
        if (column.func) {
            return column.func(rawValue, row);
        } else {
            return strings.transform(rawValue, column.type, column.defaultValue);
        }
    }
}
